import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";

export const SOURCE_KEY = { admin: "admin.sourceAdmin", stripe: "admin.sourceStripe", trial: "admin.sourceTrial" };
const TIER_LABEL = { free: "Free", starter: "Starter", pro: "Pro", pro_plus: "Pro+" };

export const fmtDate = (iso, lang) => (iso ? new Date(iso).toLocaleDateString(lang) : "");
export const fmtDateTime = (iso, lang) => (iso ? new Date(iso).toLocaleString(lang, { dateStyle: "short", timeStyle: "short" }) : "");

export function PlanActionButton({ u, onSetTier, t }) {
  return u.plan?.is_premium ? (
    <button onClick={() => onSetTier(u.id, "free")} className="text-xs text-slate-400 hover:text-slate-300" data-testid={`admin-remove-premium-${u.id}`}>{t("admin.removePremium")}</button>
  ) : (
    <button onClick={() => onSetTier(u.id, "pro")} className="text-xs text-amber-400 hover:text-amber-300" data-testid={`admin-upgrade-${u.id}`}>{t("admin.promotePremium")}</button>
  );
}

export function PlanCell({ uid, plan, t }) {
  const { i18n } = useTranslation();
  const p = plan || { tier: "free" };
  const d = (iso) => fmtDate(iso, i18n.language);
  let note = null;
  if (p.is_premium) note = p.expires_at ? t("admin.expiresOn", { date: d(p.expires_at) }) : t("admin.noExpiry");
  else if (p.trial_active) note = t("admin.trialUntil", { date: d(p.trial_ends_at) });
  const trialOverride = p.is_premium && p.trial_active && p.effective_tier && p.effective_tier !== p.tier;
  return (
    <div data-testid={`admin-plan-${uid}`}>
      <span className={`text-[10px] label-tech px-2 py-1 rounded border ${p.is_premium ? "bg-amber-500/10 text-amber-300 border-amber-500/40" : "bg-slate-500/10 text-slate-300 border-slate-500/40"}`} data-testid={`admin-tier-${uid}`}>
        {(p.tier || "free").toUpperCase()}
      </span>
      {p.premium_source && (
        <span className="ml-2 text-[10px] text-slate-400" data-testid={`admin-source-${uid}`}>{t(SOURCE_KEY[p.premium_source])}</span>
      )}
      {note && <div className="mt-1 text-[10px] text-slate-500" data-testid={`admin-expiry-${uid}`}>{note}</div>}
      {trialOverride && (
        <div className="text-[10px] text-blue-300/80" data-testid={`admin-trial-effective-${uid}`}>
          {t("admin.trialEffective", { tier: TIER_LABEL[p.effective_tier] || p.effective_tier, date: d(p.trial_ends_at) })}
        </div>
      )}
    </div>
  );
}

export default function AdminUsersTab({ users, query, onQueryChange, onSetTier, onOpenUser, t }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={query} onChange={(e) => onQueryChange(e.target.value)} placeholder={t("admin.searchUsers")}
            className="w-full h-9 pl-8 pr-3 rounded-md bg-slate-900 border border-slate-700 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-500"
            data-testid="admin-users-search" />
        </div>
        <span className="text-xs text-slate-500 font-mono-data" data-testid="admin-users-count">{t("admin.usersCount", { count: users.length })}</span>
      </div>
      <div className="tech-card overflow-hidden" data-testid="admin-users">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 text-slate-500 text-xs">
              <th className="text-left px-4 py-2 label-tech">{t("admin.thEmail")}</th>
              <th className="text-left px-4 py-2 label-tech">{t("admin.thName")}</th>
              <th className="text-left px-4 py-2 label-tech">{t("admin.thTier")}</th>
              <th className="text-right px-4 py-2 label-tech">{t("admin.thQuotes")}</th>
              <th className="text-right px-4 py-2 label-tech">{t("admin.thActions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {users.map((u) => (
              <tr key={u.id} data-testid={`admin-user-${u.id}`} className="hover:bg-slate-800/30">
                <td className="px-4 py-3 text-slate-100 font-mono-data text-xs">{u.email}</td>
                <td className="px-4 py-3 text-slate-300">{u.name}</td>
                <td className="px-4 py-3"><PlanCell uid={u.id} plan={u.plan} t={t} /></td>
                <td className="px-4 py-3 text-right font-mono-data text-slate-200">{u.quotes_count}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-4">
                    <PlanActionButton u={u} onSetTier={onSetTier} t={t} />
                    <button onClick={() => onOpenUser(u.id)} className="text-xs text-slate-300 hover:text-amber-300 underline-offset-2 hover:underline" data-testid={`admin-open-user-${u.id}`}>{t("admin.details")}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
